import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import BrandLogo from '../components/brand/BrandLogo';
import Input from '../components/ui/Input';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getFriendlyError } from '../utils/friendlyError';
import { getDashboardPath } from '../utils/rolePaths';
import { isBookRedirect } from '../utils/bookingAuth';

const LoginPage = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated, role, user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        login: yup
          .string()
          .required(t('auth.loginRequired'))
          .test('email-or-phone', t('auth.loginInvalid'), (value) => {
            const v = String(value || '').trim();
            if (!v) return false;
            if (v.includes('@')) return yup.string().email().isValidSync(v);
            return v.replace(/\D/g, '').length >= 9;
          }),
        password: yup.string().required(t('auth.passwordRequired')),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { login: '', password: '' },
  });

  if (isAuthenticated && role && !submitting) {
    const target = user?.mustChangePassword
      ? '/required-password-change'
      : redirect && role === 'CLIENT'
        ? redirect
        : getDashboardPath(role, user);
    return <Navigate to={target} replace />;
  }

  const onSubmit = async ({ login: loginId, password }) => {
    setSubmitting(true);
    try {
      const loggedIn = await login(loginId.trim(), password);
      showToast(t('common.welcomeBack', { name: loggedIn.firstName }), 'success');
      const target = loggedIn.mustChangePassword
        ? '/required-password-change'
        : redirect && loggedIn.role === 'CLIENT'
          ? redirect
          : getDashboardPath(loggedIn.role, loggedIn);
      // Hard navigation avoids SPA/React Query leftovers from the previous account.
      window.location.assign(target);
    } catch (err) {
      showToast(getFriendlyError(err, t('auth.invalidCredentials')), 'error');
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-50 px-4 dark:bg-[#07040f] dark:bg-aura-radial">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-aura-800 dark:bg-[#0e0818] dark:shadow-brand sm:p-8">
        <div className="flex justify-center">
          <BrandLogo to="/" size="lg" showWordmark showTagline />
        </div>
        <p className="mt-5 text-center text-sm text-zinc-600 dark:text-zinc-500">{t('auth.signInTitle')}</p>
        {isBookRedirect(redirect) && (
          <p className="mt-2 rounded-lg border border-glow-600/30 bg-glow-600/10 px-3 py-2 text-sm text-glow-300">
            {t('auth.bookRedirectHint')}
          </p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4">
          <div>
            <Input
              label={`${t('auth.loginId')} *`}
              id="login"
              type="text"
              autoComplete="username"
              {...register('login')}
            />
            {errors.login && (
              <p className="mt-1 text-xs text-red-400">{errors.login.message}</p>
            )}
          </div>
          <div>
            <Input
              label={`${t('auth.password')} *`}
              id="password"
              type="password"
              {...register('password')}
            />
            {errors.password && (
              <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
            )}
          </div>
          <div className="text-right">
            <span className="cursor-not-allowed text-xs text-zinc-600 dark:text-zinc-500">
              {t('auth.forgotPassword')}
            </span>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white transition hover:bg-glow-500 disabled:opacity-50"
          >
            {submitting && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
            {submitting ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-600 dark:text-zinc-500">
          {t('auth.newClient')}{' '}
          <Link
            to={redirect ? `/register?redirect=${encodeURIComponent(redirect)}` : '/register'}
            className="text-glow-400 hover:underline"
          >
            {t('auth.createAccount')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-zinc-600 dark:text-zinc-500">
          {t('auth.salonStaff')}{' '}
          <Link to="/register-staff" className="text-glow-400 hover:underline">
            {t('auth.staffRegister.link')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-zinc-600 dark:text-zinc-500">
          {t('auth.salonOwner')}{' '}
          <Link to="/request-tenant" className="text-glow-400 hover:underline">
            {t('auth.partnerWithUs')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
